import type { CostBenefitSide } from "@repo/shared/types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { useComparisonSideLabels } from "../comparison-layout";
import { T } from "./metrics-comparison-table.i18n";

type MetricsComparisonTableProps = {
  existing: CostBenefitSide;
  proposed: CostBenefitSide;
  /**
   * Agreement follow-ups name the savings plan; account follow-ups keep
   * "Portfolio". Forward-compat only: the snapshot is one-time-only for now and
   * agreement follow-ups are locked to recurring (QL-309), so the savingsPlan
   * strings are unreachable until recurring support lands.
   */
  variant: "portfolio" | "savingsPlan";
};

/** Existing vs modified: expected return, expected volatility, product costs — all annual %. */
export function MetricsComparisonTable({
  existing,
  proposed,
  variant,
}: MetricsComparisonTableProps) {
  const t = useT(T);
  const { formatNumber } = useRegionalFormat();
  // The side headers come from the shared comparison layout's strings.
  const labels = useComparisonSideLabels(variant);
  const pct = (v: number | null) =>
    v == null
      ? "–"
      : `${formatNumber(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`;

  const rows: Array<{ label: string; existing: number | null; proposed: number | null }> = [
    {
      label: t.expectedReturn,
      existing: existing.expectedReturnPct,
      proposed: proposed.expectedReturnPct,
    },
    {
      label: t.expectedVolatility,
      existing: existing.volatilityPct,
      proposed: proposed.volatilityPct,
    },
    { label: t.productCosts, existing: existing.productCostPct, proposed: proposed.productCostPct },
  ];

  return (
    <Table variant="data" data-testid="cost-benefit-metrics">
      <TableHeader>
        <TableRow>
          <TableHead />
          <TableHead className="text-right">{labels.existing}</TableHead>
          <TableHead className="text-right">{labels.modified}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.label}>
            <TableCell className="font-medium">{row.label}</TableCell>
            <TableCell className="text-right tabular-nums">{pct(row.existing)}</TableCell>
            <TableCell className="text-right tabular-nums">{pct(row.proposed)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
